'use client';

import { AlertCircle } from 'lucide-react';
import Link from 'next/link';
import { createContext, useContext } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import type { LucideIcon } from 'lucide-react';

export type MessageDisplaySize = 'sm' | 'default' | 'lg' | 'page';
export type MessageDisplayIntent = 'error' | 'info' | 'success';

type MessageDisplayContextValue = {
  size: MessageDisplaySize;
  intent: MessageDisplayIntent;
};

const MessageDisplayContext = createContext<MessageDisplayContextValue | null>(null);

const useMessageDisplayContext = () => {
  const context = useContext(MessageDisplayContext);
  if (!context) {
    throw new Error('MessageDisplay components must be used within MessageDisplayRoot');
  }
  return context;
};

export type MessageDisplayRootProps = React.ComponentProps<'div'> & {
  size?: MessageDisplaySize;
  intent?: MessageDisplayIntent;
};

export const MessageDisplayRoot: React.FC<MessageDisplayRootProps> = ({
  size = 'default',
  intent = 'info',
  className,
  children,
  ...props
}) => {
  return (
    <MessageDisplayContext.Provider value={{ size, intent }}>
      <div
        className={cn(
          'relative flex flex-1 flex-col items-center justify-center overflow-hidden text-center',
          size === 'sm' && 'px-4 py-8',
          size === 'default' && 'px-6 py-12',
          size === 'lg' && 'px-8 py-16',
          size === 'page' && 'h-full px-8 py-20',
          className,
        )}
        {...props}
      >
        {size === 'page' && <MessageDisplayBackground />}
        <div
          className={cn(
            'relative z-10 flex flex-col items-center',
            size === 'sm' && 'gap-3',
            size === 'default' && 'gap-4',
            size === 'lg' && 'gap-5',
            size === 'page' && 'gap-6',
          )}
        >
          {children}
        </div>
      </div>
    </MessageDisplayContext.Provider>
  );
};

export type MessageDisplayBackgroundProps = React.ComponentProps<'div'>;

export const MessageDisplayBackground: React.FC<MessageDisplayBackgroundProps> = ({
  className,
  ...props
}) => {
  const { intent } = useMessageDisplayContext();

  return (
    <div className={className} {...props}>
      <div
        className={cn(
          'absolute inset-0 bg-linear-to-b to-transparent',
          intent === 'error' && 'from-destructive/2',
          intent === 'info' && 'from-primary/2',
          intent === 'success' && 'from-success/2',
        )}
      />
      <div
        className={cn(
          'absolute top-1/2 left-1/2 size-150 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl',
          intent === 'error' && 'bg-muted/50',
          intent === 'info' && 'bg-primary/3',
          intent === 'success' && 'bg-success/3',
        )}
      />
      {intent === 'error' ? (
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      ) : (
        <>
          <div className="absolute top-1/4 right-1/4 size-32 rounded-full bg-primary/5 blur-2xl" />
          <div className="absolute bottom-1/4 left-1/4 size-24 rounded-full bg-primary/4 blur-2xl" />
        </>
      )}
    </div>
  );
};

export type MessageDisplayIconProps = React.ComponentProps<'div'> & {
  icon?: LucideIcon;
};

export const MessageDisplayIcon: React.FC<MessageDisplayIconProps> = ({
  className,
  icon: Icon = AlertCircle,
  ...props
}) => {
  const { size, intent } = useMessageDisplayContext();

  return (
    <div className={cn('relative', size === 'page' && 'mb-4', className)} {...props}>
      {(size === 'lg' || size === 'page') && (
        <div
          className={cn(
            'absolute inset-0 rounded-full',
            intent === 'error' && 'bg-destructive/5',
            intent === 'info' && 'bg-primary/5',
            intent === 'success' && 'bg-success/5',
            size === 'page' && 'scale-150',
            size === 'lg' && 'scale-125',
          )}
        />
      )}
      <div
        className={cn(
          'relative flex items-center justify-center rounded-full',
          intent === 'error' &&
            `bg-linear-to-br from-destructive/15 via-destructive/10 to-transparent ring-1 ring-destructive/10`,
          intent === 'info' &&
            `bg-linear-to-br from-primary/15 via-primary/10 to-transparent ring-1 ring-primary/10`,
          intent === 'success' &&
            `bg-linear-to-br from-success/15 via-success/10 to-transparent ring-1 ring-success/10`,
          size === 'sm' && 'size-12',
          size === 'default' && 'size-16',
          size === 'lg' && 'size-20',
          size === 'page' && 'size-28',
        )}
      >
        <Icon
          className={cn(
            intent === 'error' && 'text-destructive',
            intent === 'info' && 'text-primary',
            intent === 'success' && 'text-success',
            size === 'sm' && 'size-5',
            size === 'default' && 'size-7',
            size === 'lg' && 'size-9',
            size === 'page' && 'size-12',
          )}
        />
      </div>
      {size === 'page' && intent === 'info' && (
        <>
          <div className={cn('absolute -top-2 -right-2 size-2 rounded-full', `bg-primary/20`)} />
          <div
            className={cn('absolute -bottom-1 -left-3 size-1.5 rounded-full', 'bg-primary/15')}
          />
        </>
      )}
    </div>
  );
};

export type MessageDisplayErrorCodeProps = React.ComponentProps<'div'> & {
  code: string | number;
};

export const MessageDisplayErrorCode: React.FC<MessageDisplayErrorCodeProps> = ({
  className,
  code,
  ...props
}) => {
  const { size } = useMessageDisplayContext();

  return (
    <div className={cn('relative', className)} {...props}>
      <span
        className={cn(
          'font-bold tracking-tight select-none',
          size === 'page' &&
            'bg-linear-to-b from-foreground/15 to-foreground/5 bg-clip-text text-[120px] leading-none text-transparent md:text-[180px]',
          size === 'lg' && 'text-8xl text-muted-foreground/20',
          size === 'default' && 'text-6xl text-muted-foreground/20',
          size === 'sm' && 'text-4xl text-muted-foreground/20',
        )}
      >
        {code}
      </span>
      {size === 'page' && (
        <>
          <div className="absolute -top-4 -right-8 size-3 rounded-full bg-primary/20" />
          <div className="absolute -bottom-2 -left-6 size-2 rounded-full bg-destructive/20" />
          <div className="absolute top-1/2 -right-12 size-1.5 rounded-full bg-muted-foreground/20" />
        </>
      )}
    </div>
  );
};

export type MessageDisplayContentProps = React.ComponentProps<'div'>;

export const MessageDisplayContent: React.FC<MessageDisplayContentProps> = ({
  className,
  ...props
}) => {
  return <div className={cn('space-y-2', className)} {...props} />;
};

export type MessageDisplayTitleProps = React.ComponentProps<'h1'>;

export const MessageDisplayTitle: React.FC<MessageDisplayTitleProps> = ({
  className,
  ...props
}) => {
  const { size } = useMessageDisplayContext();

  return (
    <h1
      className={cn(
        'font-semibold text-balance text-foreground',
        size === 'sm' && 'text-base',
        size === 'default' && 'text-lg',
        size === 'lg' && 'text-xl',
        size === 'page' && 'text-2xl md:text-3xl',
        className,
      )}
      {...props}
    />
  );
};

export type MessageDisplayDescriptionProps = React.ComponentProps<'p'>;

export const MessageDisplayDescription: React.FC<MessageDisplayDescriptionProps> = ({
  className,
  ...props
}) => {
  const { size } = useMessageDisplayContext();

  return (
    <p
      className={cn(
        'leading-relaxed text-balance text-muted-foreground',
        size === 'sm' && 'max-w-sm text-sm',
        size === 'default' && 'max-w-md text-sm',
        size === 'lg' && 'max-w-lg text-base',
        size === 'page' && 'max-w-xl text-base md:text-lg',
        className,
      )}
      {...props}
    />
  );
};

export type MessageDisplayActionsProps = React.ComponentProps<'div'>;

export const MessageDisplayActions: React.FC<MessageDisplayActionsProps> = ({
  className,
  ...props
}) => {
  const { size } = useMessageDisplayContext();

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-center',
        size === 'page' && 'mt-4 gap-4',
        (size === 'sm' || size === 'default' || size === 'lg') && 'mt-2 gap-3',
        className,
      )}
      {...props}
    />
  );
};

export type MessageDisplayButtonProps = React.ComponentProps<typeof Button>;

export const MessageDisplayButton: React.FC<MessageDisplayButtonProps> = ({
  className,
  ...props
}) => {
  const { size } = useMessageDisplayContext();

  return (
    <Button
      variant="outline"
      size={size === 'page' ? 'lg' : 'default'}
      className={className}
      {...props}
    />
  );
};

export type MessageDisplayPrimaryButtonProps = React.ComponentProps<typeof Button> & {
  href?: string;
};

export const MessageDisplayPrimaryButton: React.FC<MessageDisplayPrimaryButtonProps> = ({
  className,
  href,
  children,
  ...props
}) => {
  const { size } = useMessageDisplayContext();

  return (
    <Button
      size={size === 'page' ? 'lg' : 'default'}
      className={className}
      asChild={!!href}
      {...props}
    >
      {href ? <Link href={href}>{children}</Link> : children}
    </Button>
  );
};

export type MessageDisplayProps = {
  title: React.ReactNode;
  description: React.ReactNode;
  icon?: LucideIcon;
  errorCode?: string | number;
  intent?: MessageDisplayIntent;
  size?: MessageDisplaySize;
  children?: React.ReactNode;
  className?: string;
};

/**
 * Convenience MessageDisplay with most of the default behavior pre built
 */
export const MessageDisplay: React.FC<MessageDisplayProps> = ({
  title,
  description,
  icon = AlertCircle,
  errorCode,
  intent = 'info',
  size = 'lg',
  children,
  className,
}) => {
  return (
    <MessageDisplayRoot size={size} intent={intent} className={className}>
      {icon && !errorCode && <MessageDisplayIcon icon={icon} />}
      {errorCode && <MessageDisplayErrorCode code={errorCode} />}
      <MessageDisplayContent>
        <MessageDisplayTitle>{title}</MessageDisplayTitle>
        <MessageDisplayDescription>{description}</MessageDisplayDescription>
      </MessageDisplayContent>
      {children && <MessageDisplayActions>{children}</MessageDisplayActions>}
    </MessageDisplayRoot>
  );
};
